'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { CONFIG } from '@frameio/config';
import { ConnectDisplay } from '@frameio/connections/shared';
import {
  AvatarForPerson,
  type PersonAvatarShape,
} from '@frameio/shared/components/AvatarForPerson';
import { useActiveAccountId } from '@frameio/shared/contexts/ActiveEntitiesContext';
import { useMeCachedQuery } from '@frameio/shared/hooks';
import {
  AlertInline,
  Box,
  Button2,
  Dialog,
  DialogActions,
  DialogFooter,
  DialogHeader,
  IconButton2,
  InlineButton,
  Select,
  Spinner,
  Text,
  theme,
} from '@frameio/vapor';
import FrameIOPNG from '@frameio/vapor-graphics/raster/adobe-logos/frame.io@1x.png';
import FrameIOSVG from '@frameio/vapor-graphics/vector/adobe-logos/frame.io.svg';
import {
  IconChevronDown,
  IconChevronRight,
  IconWarningFill,
  IconX,
} from '@frameio/vapor-icons';
import { useGetAssetsBrandValidationMetadataQuery } from '../../gql/queries/generated/GetAssetsBrandValidationMetadata';
import { useBrandsForUbs } from '../../hooks/useBrandsForUbs';
import { useCategoriesForBrand } from '../../hooks/useCategoriesForBrand';
import { useUbsValidationAction } from '../../hooks/useUbsValidationAction';
import {
  type BrandValidationFailure,
  toBrandValidationInputs,
  validateBrandValidationAssets,
} from '../../utils';
import AppIconPNG from './app-icon.png';
import TEXT from './generated/TEXT/UbsValidateDialog';
import styles from './UbsValidateDialog.module.css';

TEXT.DEFINE({
  /** Title of the brand validation dialog. */
  title: 'Run Brand Validation',
  /** Source app label shown beneath the title. */
  appLabel: 'Powered by Adobe Brand Intelligence',
  /** Alt text for the app icon image in the dialog header. */
  appIconAlt: 'Powered by Adobe Brand Intelligence',
  /** Title shown on the Adobe IMS connect step (before the user is signed in). */
  connectTitle: 'Connect Adobe Brand Services',
  /** Subtitle shown on the Adobe IMS connect step. */
  connectSubtitle:
    'To use this action, you must connect your Frame.io account with Adobe Brand Services.',
  /** Label under the Frame.io source icon on the connect step. */
  connectSourceLabel: 'Frame.io',
  /** Label under the destination icon on the connect step. */
  connectDestinationLabel: 'Adobe Brand Services',
  /** Alt text for the destination icon on the connect step. */
  connectDestinationIconAlt: 'Adobe Brand Services',
  /** Label for the Learn More link on the connect step. */
  connectLearnMore: 'Learn More',
  /** Connect button label on the connect step. */
  connect: 'Connect',
  /** Label above the brand select. */
  brandLabel: 'Brand',
  /** Placeholder shown in the select before a brand is chosen. */
  brandPlaceholder: 'Select a brand',
  /** Placeholder shown while brands are being fetched. */
  brandLoadingPlaceholder: 'Loading brands…',
  /** Label above the category select. */
  categoryLabel: 'Category',
  /** Placeholder shown in the category select before a category is chosen. */
  categoryPlaceholder: 'Select a category',
  /** Placeholder shown while categories are being fetched. */
  categoryLoadingPlaceholder: 'Loading categories…',
  /** Description of what the agent does when run. */
  description:
    'The Brand Validation agent will leave feedback as comments on selected assets.',
  /** Inline link text after the description, pointing to docs. */
  learnMore: 'Learn more',
  /** Run button label. */
  run: 'Run',
  /** Accessible label used to close the dialog. */
  dialogCloseLabel: 'Close dialog',
  /** Error message shown when the brand list fails to load. */
  loadError: 'We couldn’t load your brands. Please try again.',
  /** Error message shown when the category list fails to load. */
  categoryLoadError: 'We couldn’t load categories. Please try again.',
  /** Message shown when the account has no brands yet. */
  emptyMessage: 'No brands are available for this account.',
  /** Message shown when the selected brand has no categories. */
  categoryEmptyMessage: 'No categories are available for this brand.',
  /** Call-to-action text linking out to the brand portal. */
  createBrandLinkText: 'Create a brand in the brand portal',
  /** Summary text for the unsupported-files accordion header. */
  validationWarningSummary: ({
    failedCount,
    totalCount,
  }: {
    failedCount: number;
    totalCount: number;
  }): string =>
    `${failedCount} of ${totalCount} files have unsupported formats`,
  /** Introduction text shown when the unsupported-files accordion is expanded. */
  validationWarningIntro:
    'The following files will not be included in validation:',
  /** Continuation text shown when some files are still valid. */
  validationWarningContinue:
    'You can still run validation for the remaining files.',
  /** Reason label shown for an asset whose MIME type isn't allowed. */
  unsupportedFormatReason: (mimeType: string): string =>
    `Unsupported format: ${mimeType}`,
  /** Reason label shown when an asset's MIME type metadata isn't available. */
  missingMetadataReason: 'Metadata unavailable',
});

// This is a "go here to learn more" link, not a functional URL base
const BRAND_PORTAL_HREF =
  CONFIG.adobeIMS.brandValidation.imsEnvironment === 'prod'
    ? 'https://experience.adobe.com'
    : 'https://experience-stage.adobe.com';

const appIconSrc =
  typeof AppIconPNG === 'string' ? AppIconPNG : AppIconPNG?.src;
const frameIoSrc =
  typeof FrameIOPNG === 'string' ? FrameIOPNG : FrameIOPNG?.src;

const CONNECT_ICON_PROPS = {
  role: 'img' as const,
  width: '28px',
  height: '28px',
};

const ConnectSourceIcon = () =>
  frameIoSrc ? (
    <img
      {...CONNECT_ICON_PROPS}
      alt={TEXT.connectSourceLabel}
      data-chromatic="ignore"
      src={frameIoSrc}
    />
  ) : (
    <FrameIOSVG {...CONNECT_ICON_PROPS} alt={TEXT.connectSourceLabel} />
  );

const ConnectDestinationIcon = () =>
  appIconSrc ? (
    <img
      {...CONNECT_ICON_PROPS}
      alt={TEXT.connectDestinationIconAlt}
      data-chromatic="ignore"
      src={appIconSrc}
    />
  ) : null;

const ConnectContextAvatar = () => {
  const me = useMeCachedQuery().data?.me;
  const person: PersonAvatarShape = {
    name: me?.name,
    email: me?.email,
    avatarColor: me?.avatarColor,
    avatarUrl:
      me?.__typename === 'AuthenticatedUser'
        ? me.profileImage?.image128
        : undefined,
  };

  return <AvatarForPerson person={person} size={32} />;
};

const CHEVRON = (
  <IconChevronDown color="var(--colors-icon-secondary)" size={16} />
);

function getFailedAssetReasonLabel(failure: BrandValidationFailure): string {
  if (failure.reason === 'unsupported_format') {
    return TEXT.unsupportedFormatReason(failure.mimeType);
  }
  return TEXT.missingMetadataReason;
}

type UbsValidateDialogProps = {
  assetIds: string[];
  isShowing: boolean;
  onClose: () => void;
  totalSelectedCount: number;
};

export function UbsValidateDialog({
  assetIds,
  isShowing,
  onClose,
  totalSelectedCount,
}: UbsValidateDialogProps) {
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isWarningExpanded, setIsWarningExpanded] = useState(false);
  const accountId = useActiveAccountId();
  const {
    brands,
    error: brandsError,
    loading: brandsLoading,
    needsSignIn,
    signIn,
  } = useBrandsForUbs({ accountId });

  const { data: metadataData } = useGetAssetsBrandValidationMetadataQuery({
    variables: {
      accountId: accountId ?? '',
      assetIds,
      first: assetIds.length,
    },
    skip: !accountId || assetIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const { validAssetIds, failedAssets } = useMemo(() => {
    const inputs = toBrandValidationInputs(
      metadataData?.account?.assets?.nodes ?? []
    );
    return validateBrandValidationAssets(inputs);
  }, [metadataData]);

  // Clear the local connecting state when the dialog closes or sign-in resolves.
  // (If the user cancels the IMS popup, this state is reset the next time the
  //  dialog is closed and reopened.)
  useEffect(() => {
    if (!isShowing || !needsSignIn) setIsConnecting(false);
  }, [isShowing, needsSignIn]);

  const handleConnect = useCallback(() => {
    setIsConnecting(true);
    signIn();
  }, [signIn]);
  const {
    categories,
    error: categoriesError,
    loading: categoriesLoading,
  } = useCategoriesForBrand({
    accountId,
    brandId: selectedBrandId || null,
  });
  const { validateAssetUbs } = useUbsValidationAction();

  const brandOptions = brands.map((brand) => ({
    label: brand.name,
    value: brand.libraryUrn,
  }));
  const categoryOptions = categories.map((category) => ({
    label: category.category,
    value: category.category,
  }));

  const isBrandsEmpty = !brandsLoading && !brandsError && brands.length === 0;
  const isCategoriesEmpty =
    Boolean(selectedBrandId) &&
    !categoriesLoading &&
    !categoriesError &&
    categories.length === 0;
  const isRunDisabled =
    brandsLoading ||
    categoriesLoading ||
    brandsError ||
    categoriesError ||
    brands.length === 0 ||
    categories.length === 0 ||
    !selectedBrandId ||
    !selectedCategory ||
    validAssetIds.length === 0;

  const handleBrandChange = useCallback((value: string) => {
    setSelectedBrandId(value);
    setSelectedCategory('');
  }, []);

  const isInitialBrandsFetch =
    brandsLoading && brands.length === 0 && !brandsError;

  const handleRun = useCallback(async () => {
    if (isRunDisabled) return;
    const succeeded = await validateAssetUbs({
      assetIds: validAssetIds,
      brandId: selectedBrandId,
      category: selectedCategory,
      totalSelectedCount,
    });
    if (succeeded) onClose();
  }, [
    isRunDisabled,
    onClose,
    selectedBrandId,
    selectedCategory,
    totalSelectedCount,
    validAssetIds,
    validateAssetUbs,
  ]);

  if (isInitialBrandsFetch) {
    return (
      <Dialog
        aria-label={TEXT.title}
        enableClickOutsideToClose={true}
        isShowing={isShowing}
        onRequestClose={onClose}
        size="medium"
      >
        <div className={styles.loadingContainer}>
          <Spinner aria-label={TEXT.title} delay={0} isLoading={true} />
        </div>
      </Dialog>
    );
  }

  if (needsSignIn) {
    return (
      <Dialog
        aria-label={TEXT.connectTitle}
        enableClickOutsideToClose={true}
        isShowing={isShowing}
        onRequestClose={onClose}
        size="medium"
      >
        <DialogHeader
          closeLabel={TEXT.dialogCloseLabel}
          subtitle={TEXT.connectSubtitle}
          title={TEXT.connectTitle}
        />
        <div className={styles.connectDisplayWrapper}>
          <ConnectDisplay
            contextIcon={{ Icon: ConnectContextAvatar }}
            destinationIcon={{
              Icon: ConnectDestinationIcon,
              label: TEXT.connectDestinationLabel,
            }}
            dimensions={{ paddingTop: 18, height: 120 }}
            sourceIcon={{
              Icon: ConnectSourceIcon,
              label: TEXT.connectSourceLabel,
            }}
            state="LOOPING"
          />
        </div>
        <DialogFooter $showTopBorder={true}>
          <div>
            <Button2
              elementType="a"
              href={BRAND_PORTAL_HREF}
              rel="noopener noreferrer"
              target="_blank"
              variant="tertiary"
            >
              {TEXT.connectLearnMore}
            </Button2>
          </div>
          <DialogActions>
            <Button2
              isLoading={isConnecting}
              onPress={handleConnect}
              variant="primary"
            >
              {TEXT.connect}
            </Button2>
          </DialogActions>
        </DialogFooter>
      </Dialog>
    );
  }

  return (
    <Dialog
      aria-label={TEXT.title}
      enableClickOutsideToClose={false}
      isShowing={isShowing}
      onRequestClose={onClose}
      size="medium"
    >
      <header className={styles.header}>
        {appIconSrc && (
          <img
            alt={TEXT.appIconAlt}
            className={styles.appIcon}
            src={appIconSrc}
          />
        )}
        <div className={styles.titleBlock}>
          <Text fontWeight="medium" variant="primary">
            {TEXT.title}
          </Text>
          <Text color="secondary" fontWeight="regular" variant="secondary">
            {TEXT.appLabel}
          </Text>
        </div>
        <IconButton2
          icon={<IconX />}
          label={TEXT.dialogCloseLabel}
          onPress={onClose}
          size="regular"
          variant="tertiary"
        />
      </header>

      <div className={styles.body}>
        {brandsError && (
          <AlertInline variant="error">{TEXT.loadError}</AlertInline>
        )}
        {isBrandsEmpty && (
          <div className={styles.columnTight}>
            <Text color="error" variant="body">
              {TEXT.emptyMessage}
            </Text>
            <InlineButton
              elementType="a"
              href={BRAND_PORTAL_HREF}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Text color="link" variant="linkSmall">
                {TEXT.createBrandLinkText}
              </Text>
            </InlineButton>
          </div>
        )}
        {!brandsError && !isBrandsEmpty && (
          <div className={styles.fullWidthSelect}>
            <Select
              content={brandOptions}
              isDisabled={brandsLoading || brands.length === 0}
              label={TEXT.brandLabel}
              name="ubs-brand"
              onChange={handleBrandChange}
              placeholder={
                brandsLoading
                  ? TEXT.brandLoadingPlaceholder
                  : TEXT.brandPlaceholder
              }
              placement="bottom"
              shouldUsePortal={false}
              value={selectedBrandId}
            >
              {({ selectedOption }) => (
                <Button2 iconAfter={CHEVRON}>
                  {selectedOption?.label ??
                    (brandsLoading
                      ? TEXT.brandLoadingPlaceholder
                      : TEXT.brandPlaceholder)}
                </Button2>
              )}
            </Select>
          </div>
        )}
        {selectedBrandId && (
          <>
            {categoriesError && (
              <Text color="error" variant="body">
                {TEXT.categoryLoadError}
              </Text>
            )}
            {isCategoriesEmpty && !categoriesError && (
              <Text color="error" variant="body">
                {TEXT.categoryEmptyMessage}
              </Text>
            )}
            {!categoriesError && !isCategoriesEmpty && (
              <div className={styles.fullWidthSelect}>
                <Select
                  content={categoryOptions}
                  isDisabled={categoriesLoading || categories.length === 0}
                  label={TEXT.categoryLabel}
                  name="ubs-category"
                  onChange={setSelectedCategory}
                  placeholder={
                    categoriesLoading
                      ? TEXT.categoryLoadingPlaceholder
                      : TEXT.categoryPlaceholder
                  }
                  placement="bottom"
                  shouldUsePortal={false}
                  value={selectedCategory}
                >
                  {({ selectedOption }) => (
                    <Button2 iconAfter={CHEVRON}>
                      {selectedOption?.label ??
                        (categoriesLoading
                          ? TEXT.categoryLoadingPlaceholder
                          : TEXT.categoryPlaceholder)}
                    </Button2>
                  )}
                </Select>
              </div>
            )}
          </>
        )}
        {failedAssets.length > 0 && (
          <div className={styles.validationAccordion}>
            <button
              aria-expanded={isWarningExpanded}
              className={styles.validationAccordionHeader}
              onClick={() => setIsWarningExpanded((v) => !v)}
              type="button"
            >
              <Box alignItems="center" display="flex" gap={8}>
                <IconWarningFill
                  color={theme.colors.meta.icon.yellow}
                  size={16}
                />
                <Text variant="body">
                  {TEXT.validationWarningSummary({
                    failedCount: failedAssets.length,
                    totalCount: assetIds.length,
                  })}
                </Text>
              </Box>
              <IconChevronRight
                className={clsx(styles.chevron, {
                  [styles.rotated]: isWarningExpanded,
                })}
                size={16}
              />
            </button>
            {isWarningExpanded && (
              <div className={styles.validationAccordionContent}>
                <Text color="secondary" variant="body">
                  {TEXT.validationWarningIntro}
                </Text>
                <div className={styles.validationListContainer}>
                  <ul className={styles.validationList}>
                    {failedAssets.map((asset) => (
                      <li key={asset.id} className={styles.validationListItem}>
                        <div className={styles.validationItemContent}>
                          <Text
                            as="span"
                            display="block"
                            fontWeight="semiBold"
                            variant="body"
                          >
                            {asset.name || asset.id}
                          </Text>
                          <Text
                            as="span"
                            color="secondary"
                            display="block"
                            variant="body"
                          >
                            {getFailedAssetReasonLabel(asset)}
                          </Text>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                {validAssetIds.length > 0 && (
                  <Text color="secondary" variant="body">
                    {TEXT.validationWarningContinue}
                  </Text>
                )}
              </div>
            )}
          </div>
        )}
        <Text color="secondary" variant="body">
          {TEXT.description}{' '}
          <InlineButton
            elementType="a"
            href={BRAND_PORTAL_HREF}
            rel="noopener noreferrer"
            target="_blank"
            variant="primary"
          >
            <Text color="link" fontWeight="regular" variant="body">
              {TEXT.learnMore}
            </Text>
          </InlineButton>
        </Text>
      </div>

      <DialogFooter>
        <DialogActions>
          <Button2
            isDisabled={isRunDisabled}
            onPress={handleRun}
            variant="primary"
          >
            {TEXT.run}
          </Button2>
        </DialogActions>
      </DialogFooter>
    </Dialog>
  );
}
